import React, { useMemo } from 'react';
import type { CSSProperties } from 'react';
import styled from 'styled-components';
import { rem } from '@packages/ui/tokens';
import type { FormColumnGroupNode } from '../../types/layout';
import { FormFieldsRenderer } from '../fields/FormFieldsRenderer';

interface ColumnGroupRendererProps {
  node: FormColumnGroupNode;
  namePrefix?: string;
}

const GroupGrid = styled.div<{ $count: number; $columnTemplate?: string }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(24)};
  width: 100%;
  min-width: 0;

  @media (min-width: 640px) {
    grid-template-columns: ${({ $columnTemplate, $count }) =>
      $columnTemplate ?? `repeat(${$count}, minmax(0, 1fr))`};
  }

  &:not(:first-child) {
    margin-top: ${rem(32)};
  }
`;

const GroupColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  min-width: 0;
  width: 100%;
  --matrix-row-gap: ${rem(4)};
`;

const GroupTitle = styled.div`
  font-family: var(--ui-font-heading, var(--ui-font-sans, 'HK Grotesk', sans-serif));
  font-size: ${rem(20)};
  font-weight: 400;
  line-height: ${rem(24)};
  color: #595959;
  margin-bottom: 0;
`;

export const FormColumnGroupRenderer = React.memo(function FormColumnGroupRenderer({
  node,
  namePrefix,
}: ColumnGroupRendererProps): React.ReactElement {
  const gridStyle = useMemo((): CSSProperties | undefined => {
    if (node.style == null) return undefined;
    return node.style as CSSProperties;
  }, [node.style]);

  return (
    <GroupGrid
      $count={node.groups.length}
      $columnTemplate={node.columnTemplate}
      data-form-column-group={node.semanticId}
      style={gridStyle}
    >
      {node.groups.map((group, idx) => (
        <GroupColumn key={idx}>
          {group.title != null && group.title !== '' && (
            <GroupTitle>{group.title}</GroupTitle>
          )}
          <FormFieldsRenderer
            node={{ type: 'fields', fields: group.fields }}
            namePrefix={namePrefix}
          />
        </GroupColumn>
      ))}
    </GroupGrid>
  );
});
